import Backbone from 'backbone';
import classNames from 'classnames';
import $ from 'jquery';
import React, {MutableRefObject, useCallback, useLayoutEffect, useRef} from 'react';

import {resizeThrottler} from 'BenchlingUtil';
import {TabViewWithOptionalAfterAttachCallback} from 'buffers/BufferUtil';
import {TabState} from 'Navigation';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';
import {GenericBackboneViewClass, Optional} from 'util/Types';

interface BufferViewLayoutAttributes {
  disableResize: boolean;
  el: null | JQuery;
  width: number;
  height: number;
  menu: boolean;
}

interface BufferViewLayout {
  get<K extends keyof BufferViewLayoutAttributes>(key: K): BufferViewLayoutAttributes[K];

  set<K extends keyof BufferViewLayoutAttributes>(
    attributeName: K,
    value: BufferViewLayoutAttributes[K],
    options?: Backbone.ModelSetOptions
  ): BufferViewLayout;

  set(obj: Partial<BufferViewLayoutAttributes>): BufferViewLayout;
}

/*
 * BufferViewLayout: model class that allows for imperatively resizing tabs. Currently used
 * only by sequence maps.
 */
class BufferViewLayout extends Backbone.Model {
  defaults(): BufferViewLayoutAttributes {
    return {
      disableResize: false,
      el: null,
      width: 0,
      height: 0,
      menu: false,
    };
  }

  resize(): void {
    // In some cases we want to disable updates based on resizing, like when rendering a tab for
    // a PDF where we want to hard-code the dimensions.
    if (this.get('disableResize')) {
      return;
    }
    if (this.get('el')!.find('.buffer-tab-wrapper').width() !== this.get('width')) {
      this.set({
        width: this.get('el')!.find('.buffer-tab-wrapper').width(),
      });
    }
    if (this.get('el')!.height() !== this.get('height')) {
      this.set({height: this.get('el')!.height()});
    }
  }
}

// NOTE: this class was intended to be replaced by the BufferContent component
// in https://github.com/benchling/aurelia/pull/45798/ but caused rendering issues
// with Tooltips not being removed when changing buffers. Keeping them both for now
// since the BufferContent component can be used for rendering tabs in Storybook.
interface BufferContentViewTabView extends Backbone.View<TabState> {
  afterAttach?(): void;
}

class BufferContentView extends Backbone.View<BufferViewLayout> {
  tabView: Optional<BufferContentViewTabView>;

  static initClass(): void {
    this.prototype.className = 'hide';
  }

  initialize(options: Backbone.ViewOptions<BufferViewLayout>): void {
    this.model = new BufferViewLayout({
      el: this.$el,
      width: this.$el.find('.buffer-tab-wrapper').width(),
      height: this.$el.height(),
    });
    $(window).on(
      'resize',
      resizeThrottler(() => {
        this.model.resize();
      })
    );
    $(window).on('buffersResize', () => {
      this.model.resize();
    });
  }

  update(
    tabState: TabState | null,
    tabViewCls: GenericBackboneViewClass<TabState, BufferContentViewTabView> | null
  ): void {
    if (this.tabView != null) {
      this.tabView.remove();
      this.tabView = null;
    }

    if (tabState != null) {
      tabState.set({bufferViewLayout: this.model});

      if (tabViewCls == null) {
        throw new Error('Instantiated TabState object has no associated TabView to show');
      }
      // eslint-disable-next-line new-cap
      this.tabView = new tabViewCls({model: tabState});
      this.$el.toggleClass('buffer-options-align', tabState.get('options'));
      this.$el.toggleClass('buffer-content--grey', tabState.get('greyTab'));
      this.$el.find('.buffer-tab-wrapper').html(this.tabView.render().el);
      if (typeof this.tabView.afterAttach === 'function') {
        this.tabView.afterAttach();
      }

      // Recalculate layout height after render.
      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      return this.model.resize();
    }
  }
}
BufferContentView.initClass();

function TabOptions({
  tabState,
  TabOptionsViewCls,
}: {
  tabState: Optional<TabState>;
  TabOptionsViewCls: Optional<GenericBackboneViewClass<TabState, Backbone.View<TabState>>>;
}): JSX.Element {
  const divRef = useRef<null | HTMLDivElement>(null);
  const tabOptionsViewRef = useRef<Optional<Backbone.View<TabState>>>(null);

  useLayoutEffect(() => {
    if (tabState && TabOptionsViewCls && divRef.current) {
      const tabOptionsView = new TabOptionsViewCls({model: tabState});
      $(divRef.current).html(tabOptionsView.render().el);
      tabOptionsViewRef.current = tabOptionsView;
    }
    return () => {
      tabOptionsViewRef.current?.remove();
      tabOptionsViewRef.current = null;
    };
  });
  // Always render div regardless of props, although it may be styled as `{display: none}`
  // when a parent element has the `hide-options-view` class.
  return <div ref={divRef} className="buffer-options buffer-options--builtin" />;
}

export function useBufferViewLayout(
  tabContentDivRef: MutableRefObject<HTMLDivElement | null>
): BufferViewLayout {
  const bufferViewLayout = useRef(new BufferViewLayout());
  const {height, width} = useResizeObserver<HTMLDivElement>({ref: tabContentDivRef});
  const throttledResizeCallback = useCallback(() => {
    resizeThrottler(() => {
      bufferViewLayout.current.resize();
    })();
  }, [bufferViewLayout]);

  useLayoutEffect(() => {
    if (tabContentDivRef.current) {
      bufferViewLayout.current.set({
        el: $(tabContentDivRef.current),
      });
      bufferViewLayout.current.resize();
    }
  }, [tabContentDivRef]);

  useLayoutEffect(() => {
    throttledResizeCallback();
  }, [throttledResizeCallback, height, width]);

  return bufferViewLayout.current;
}

function TabContent({
  tabState,
  TabContentViewCls,
}: {
  tabState: Optional<TabState>;
  TabContentViewCls: Optional<GenericBackboneViewClass<TabState, TabViewWithOptionalAfterAttachCallback>>;
}): JSX.Element {
  const divRef = useRef<null | HTMLDivElement>(null);
  const tabContentDivRef = useRef<null | HTMLDivElement>(null);
  const tabContentViewRef = useRef<Optional<TabViewWithOptionalAfterAttachCallback>>(null);
  const bufferViewLayout = useBufferViewLayout(tabContentDivRef);

  useLayoutEffect(() => {
    if (tabState && TabContentViewCls && divRef.current) {
      tabState.set({bufferViewLayout});
      const tabContentView = new TabContentViewCls({model: tabState});
      $(divRef.current).html(tabContentView.render().el);
      if (typeof tabContentView.afterAttach === 'function') {
        tabContentView.afterAttach();
      }
      tabContentViewRef.current = tabContentView;
      // Recalculate layout height after render.
      bufferViewLayout.resize();
    }
    return () => {
      tabContentViewRef.current?.remove();
      tabContentViewRef.current = null;
    };
  });

  return (
    <div
      ref={tabContentDivRef}
      className={classNames('buffer-content', {
        'buffer-options-align': tabState?.get('options'),
        'buffer-content-grey': tabState?.get('greyTab'),
      })}
    >
      <div className="buffer">
        <div ref={divRef} className="buffer-tab-wrapper" />
      </div>
    </div>
  );
}

function BufferContentComponent({
  tabState,
  TabOptionsViewCls,
  TabContentViewCls,
}: {
  tabState: Optional<TabState>;
  TabOptionsViewCls: Optional<GenericBackboneViewClass<TabState, Backbone.View<TabState>>>;
  TabContentViewCls: Optional<GenericBackboneViewClass<TabState, TabViewWithOptionalAfterAttachCallback>>;
}): JSX.Element {
  // cleanup: could apply hide-options-view class at this level instead of higher in the tree.
  return (
    <div className="buffer-contentWrapper-reset">
      <TabOptions tabState={tabState} TabOptionsViewCls={TabOptionsViewCls} />
      <TabContent tabState={tabState} TabContentViewCls={TabContentViewCls} />
    </div>
  );
}

const BufferContent = React.memo(BufferContentComponent);

export {BufferContent, BufferContentView, TabOptions, TabContent, BufferViewLayout};
